import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { IvyChargepoint } from '../../../api/chargepoints-api.service';
import { CommonModule } from '@angular/common';
import { ChargepointCardComponent } from '../../../components/chargepoints/chargepoint-card/chargepoint-card.component';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TransactionsListComponent } from '../../../components/transactions/transactions-list/transactions-list.component';
import { JsonViewerService } from '../../../components/common/json-viewer-dialog/json-viewer.service';
import { TransactionApiService } from '../../../api/transactions-api.service';
import { TransactionMonitorDialogService } from '../../../components/transactions/transaction-monitor-dialog/transaction-monitor-dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { ChargepointTracesDialogComponent } from '../../../components/chargepoint-traces/chargepoint-traces-dialog/chargepoint-traces-dialog.component';
import { combineLatest, Observable, of, ReplaySubject, Subject, switchMap, catchError, tap } from 'rxjs';
import { ChargepointsIvyApiService, IvyCheckResult, IvyLastPlugInfo, RelayState } from '../../../api/chargepoints-ivy-api.service';
import { RefreshService } from '../../../components/common/refresh-widget/refresh.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CopiableInfoComponent } from '../../../elements/copiable-info/copiable-info.component';
import { getIvyRelayStateDisplay, IvyRelayStateDisplay } from './ivy-relay-state.util';

@Component({
  selector: 'app-ivy-chargepoint-page',
  templateUrl: './ivy-chargepoint-page.component.html',
  styleUrls: ['./ivy-chargepoint-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ChargepointCardComponent,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatTooltipModule,
    TransactionsListComponent,
    MatProgressBarModule,
    CopiableInfoComponent,
  ]
})
export class IvyChargepointPageComponent implements OnInit {

  @Input()
  chargepoint!: IvyChargepoint;

  @Output()
  refresh = new EventEmitter<void>();

  public plugInfo$: Observable<IvyLastPlugInfo | null>;
  public relayStates$: Observable<RelayState[] | null>;
  public connectivityCheck$: Subject<IvyCheckResult | null> = new ReplaySubject<IvyCheckResult | null>(1);

  public loadingPlugInfo = false;
  public loadingRelayState = false;
  public plugInfoError = false;
  public relayStateError = false;

  private readonly ivyDataTrigger$ = new ReplaySubject<void>(1);

  constructor(
    private chargepointsIvyApiService: ChargepointsIvyApiService,
    private transactionApiService: TransactionApiService,
    private transactionMonitorDialogService: TransactionMonitorDialogService,
    private snackBar: MatSnackBar,
    public jsonViewerService: JsonViewerService,
    private dialog: MatDialog,
    private refreshService: RefreshService,
  ) {
    this.plugInfo$ = combineLatest([
      this.ivyDataTrigger$,
      this.refreshService.refreshTrigger,
    ]).pipe(
      tap(() => {
        this.loadingPlugInfo = true;
        this.plugInfoError = false;
      }),
      switchMap(() => this.fetchPlugInfo()),
    );

    this.relayStates$ = combineLatest([
      this.ivyDataTrigger$,
      this.refreshService.refreshTrigger,
    ]).pipe(
      tap(() => {
        this.loadingRelayState = true;
        this.relayStateError = false;
      }),
      switchMap(() => this.fetchRelayState()),
    );
  }

  ngOnInit(): void {
    this.ivyDataTrigger$.next();
    this.connectivityCheck$.next(null);
  }

  public startTransaction(): void {
    if (!this.chargepoint?.plugId) {
      return;
    }
    this.transactionApiService.startTransaction({
      chargepointId: this.chargepoint.id,
      plugId: this.chargepoint.plugId,
      type: this.chargepoint.type,
    }).subscribe((transaction) => {
      this.snackBar.open('Transaction started', 'Close', { duration: 3000 });
      this.transactionMonitorDialogService.open(transaction);
      this.refresh.emit();
    });
  }

  public checkConnectivity(): void {
    if (!this.chargepoint?.plugId) {
      return;
    }
    this.chargepointsIvyApiService.checkConnectivity(this.chargepoint.plugId, new Date()).subscribe({
      next: (result) => {
        this.connectivityCheck$.next(result);
        const message = result.isReady ? 'Plug is ready' : 'Plug is not ready';
        this.snackBar.open(message, 'Close', { duration: 3000 });
      },
      error: () => {
        this.snackBar.open('Connectivity check failed', 'Close', { duration: 3000 });
      },
    });
  }

  public showLogsDialog(): void {
    this.dialog.open(ChargepointTracesDialogComponent, {
      data: { chargepoint: this.chargepoint },
      width: '850px',
      maxWidth: '850px',
    });
  }

  public getLatestRelayState(relayStates: RelayState[] | null): number | undefined {
    const indicator = relayStates?.[0]?.Indicators?.[0];
    return indicator?.Values?.relay_state;
  }

  public getRelayStateDisplay(relayStates: RelayState[] | null): IvyRelayStateDisplay {
    return getIvyRelayStateDisplay(this.getLatestRelayState(relayStates));
  }

  public getLatestRelayTimestamp(relayStates: RelayState[] | null): string | undefined {
    return relayStates?.[0]?.Indicators?.[0]?.Timestamp;
  }

  private fetchPlugInfo(): Observable<IvyLastPlugInfo | null> {
    if (!this.chargepoint?.plugId) {
      this.loadingPlugInfo = false;
      return of(null);
    }
    return this.chargepointsIvyApiService.getPlugInfo(this.chargepoint.plugId).pipe(
      tap(() => { this.loadingPlugInfo = false; }),
      catchError(() => {
        this.loadingPlugInfo = false;
        this.plugInfoError = true;
        return of(null);
      }),
    );
  }

  private fetchRelayState(): Observable<RelayState[] | null> {
    if (!this.chargepoint?.plugId) {
      this.loadingRelayState = false;
      return of(null);
    }
    return this.chargepointsIvyApiService.getRelayState(this.chargepoint.plugId).pipe(
      tap(() => { this.loadingRelayState = false; }),
      catchError(() => {
        this.loadingRelayState = false;
        this.relayStateError = true;
        return of(null);
      }),
    );
  }
}
